import type { CheckoutSession } from '@lb/domain'
import { useTranslation } from '@lb/i18n'

import { FunnelShell, PrimaryButton, SecondaryButton } from './funnel-shell'

/**
 * The four terminal outcomes (spec §3.5). All four are real states, not error
 * pages, and each has its own way forward.
 *
 * The one that shapes the others: **payment is not access.** A paid signup sits
 * in `PENDING_ACTIVATION` until Stripe's subscription webhook lands, so the
 * funnel watches rather than assuming. `ACTIVE` is terminal — a webhook
 * redelivery must not walk an activated account backwards into "setting up".
 */

export function PendingScreen({ session }: { session: CheckoutSession }) {
  const { t } = useTranslation()
  return (
    <FunnelShell
      title={t('onboarding.pendingTitle')}
      lede={t('onboarding.pendingLede')}
    >
      <div className="flex items-center gap-3 rounded-[var(--r)] border border-border bg-surface p-card">
        {/* A spinner for an action in progress is correct here — this is
            genuinely waiting on a remote event, not loading content. */}
        <span
          data-motion="essential"
          className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-border-deep border-t-ink"
        />
        <p className="text-[14px] text-ink-soft">
          {t('onboarding.awaitingPayment', { plan: session.planName })}
        </p>
      </div>

      <p className="mt-6 text-center text-[13px] text-muted">
        {t('onboarding.closeThisPage')}
      </p>
    </FunnelShell>
  )
}

/**
 * A declined card is recoverable **in place** — `DECLINED → AWAITING_PAYMENT`
 * on the same session. Nobody starts over, and nobody re-enters their details.
 */
export function DeclinedScreen({
  session,
  busy,
  onRetry,
}: {
  session: CheckoutSession
  busy?: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  return (
    <FunnelShell
      title={t('onboarding.declinedTitle')}
      lede={t('onboarding.declinedLede')}
    >
      {session.declineReason ? (
        <p
          role="alert"
          className="rounded-[var(--r)] border border-border bg-error-surface p-card text-[14px] text-error"
        >
          {session.declineReason}
        </p>
      ) : null}

      <div className="mx-auto mt-6 flex max-w-[420px] flex-col gap-3">
        <PrimaryButton busy={busy} onClick={onRetry}>
          {t('onboarding.retryPayment')}
        </PrimaryButton>
      </div>
    </FunnelShell>
  )
}

/**
 * The organisation path ends in an invoice, never a card charge. It is the only
 * path that never reaches Stripe, and access waits on finance rather than on a
 * webhook.
 */
export function InvoicedScreen({ session }: { session: CheckoutSession }) {
  const { t } = useTranslation()
  return (
    <FunnelShell
      title={t('onboarding.invoicedTitle')}
      lede={t('onboarding.invoicedLede')}
    >
      <div className="rounded-[var(--r)] border border-border bg-surface p-card">
        <p className="text-[14px] text-ink-soft">
          {session.planName} · {t('onboarding.seats', { count: session.seats })}
        </p>
      </div>

      {/*
        * Honest about a real dead end rather than silent about it: the ORG
        * checkout creates the organisation but assigns no seat, so the director
        * administers nothing until finance seats them. Raised with the backend
        * (round 3 §5); until it is resolved this is "in progress", not an error.
        */}
      <p className="mt-6 text-center text-[13px] text-muted">
        {t('onboarding.seatSetup')}
      </p>
    </FunnelShell>
  )
}

export function WelcomeScreen({
  onEnter,
  busy,
}: {
  onEnter: () => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <FunnelShell
      title={t('onboarding.welcomeTitle')}
      lede={t('onboarding.welcomeLede')}
    >
      <div className="mx-auto max-w-[420px]">
        <PrimaryButton onClick={onEnter} busy={busy}>
          {t('onboarding.enter')}
        </PrimaryButton>
      </div>
    </FunnelShell>
  )
}

/**
 * An already-registered email is never a dead end.
 *
 * The backend raises `EMAIL_ALREADY_REGISTERED` specifically so the client can
 * route to sign-in "instead of failing a form the person cannot correct".
 */
export function EmailExistsScreen({
  email,
  onSignIn,
  onBack,
}: {
  email: string
  onSignIn: () => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  return (
    <FunnelShell
      title={t('onboarding.existsTitle')}
      lede={t('onboarding.existsLede', { email })}
    >
      <div className="mx-auto flex max-w-[420px] flex-col gap-3">
        <PrimaryButton onClick={onSignIn}>{t('onboarding.signIn')}</PrimaryButton>
        <SecondaryButton onClick={onBack}>{t('onboarding.otherEmail')}</SecondaryButton>
      </div>
    </FunnelShell>
  )
}
